/**
 * ForgotPassword.tsx — Password reset request page.
 *
 * Always shows a generic success message regardless of whether the email exists,
 * so attackers cannot enumerate registered addresses.
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';

import axios from 'axios';
import API_BASE from '../../config/api';

export default function ForgotPassword() {
  const [email, setEmail]     = useState('');
  const [sent, setSent]       = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      // fire and forget — we always show the same message
      await axios.post(`${API_BASE}/auth/forgot-password`, { email: email.trim().toLowerCase() });
    } catch {
      // intentionally swallow — never reveal if the address is registered
    } finally {
      setLoading(false);
      setSent(true);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0d1117 0%, #0a1929 100%)',
        px: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 440,
          borderRadius: 3,
          border: '1px solid rgba(0,180,216,0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, gap: 1 }}>
          <SecurityIcon sx={{ fontSize: 36, color: '#00E5FF', filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
          <Typography variant="h4" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>
            Shield
          </Typography>
        </Box>
        <Typography variant="h6" textAlign="center" mt={2} mb={1}>
          Reset your password
        </Typography>

        {sent ? (
          <Box mt={3}>
            <Alert severity="success" sx={{ mb: 3 }}>
              If that email address is registered, you'll receive a reset link shortly. Check
              your inbox (and spam folder).
            </Alert>
            <Button
              component={RouterLink}
              to="/login"
              fullWidth
              variant="outlined"
              startIcon={<ArrowBackIcon />}
            >
              Back to Sign In
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                label="Email address"
                type="email"
                fullWidth
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !email}
                sx={{ fontWeight: 700, py: 1.5, mb: 2 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Send Reset Link'}
              </Button>

              <Box textAlign="center">
                <Link component={RouterLink} to="/login" variant="body2" color="primary">
                  <ArrowBackIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  Back to Sign In
                </Link>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
